/* The tidal-stream stations, loaded once and then good forever.
 *
 * Fetched LAZILY — nobody who never turns the layer on pays for it — and from a
 * static file rather than an API, so the service worker caches it like any
 * other asset and it is there at anchor with the radio off. That is the whole
 * design goal: you plan tomorrow's transit of Dodd Narrows from somewhere with
 * no signal, which rules out asking anyone at the moment you want the answer.
 *
 * A year of slack and maximum events for every current station in our waters
 * is ~105 kB gzipped. See ops/build-currents.mjs for what was left out and why.
 */

import { currentAt, near, unpack, type CurrentNow, type CurrentStation, type PackedCurrents } from '@gennetay/chart-core/lib/currents';
import { sources } from '@gennetay/chart-core/sources';

function loadHue(): 'rate' | 'phase' {
  try {
    return localStorage.getItem('gd.currents.hue') === 'phase' ? 'phase' : 'rate';
  } catch {
    return 'rate';
  }
}

class Currents {
  stations = $state<CurrentStation[]>([]);
  error = $state<string | null>(null);
  loading = $state(false);
  /** The year the shipped data covers, so the UI can say when it runs out. */
  year = $state<number | null>(null);
  /**
   * How the arrows are coloured: by RATE BAND (slack/weak/moderate/strong) or
   * by PHASE (flood cyan / ebb amber / slack grey). Phase is the difference
   * between a pleasant transit and a bad one, and a map where every arrow is
   * the same "moderate" colour hides it. Persisted, like the layer state.
   */
  hue = $state<'rate' | 'phase'>(loadHue());

  #started = false;

  setHue(hue: 'rate' | 'phase'): void {
    this.hue = hue;
    try {
      localStorage.setItem('gd.currents.hue', hue);
    } catch {
      /* not persisted — the toggle still works for the session */
    }
  }

  async load(): Promise<void> {
    if (this.#started) return;
    this.#started = true;
    this.loading = true;
    try {
      const r = await sources().fetch('static-data', 'currents-2026.json', {
        signal: AbortSignal.timeout(30_000)
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const packed = (await r.json()) as PackedCurrents;
      this.stations = unpack(packed);
      this.year = packed.year;
      this.error = null;
    } catch (e) {
      /* Retryable: a failed load must not permanently disable the layer, since
       * the usual cause is being offline before the service worker has ever
       * seen the file. */
      this.#started = false;
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.loading = false;
    }
  }

  /** Every station's state at `t`, for drawing. Stations past the end drop out. */
  at(t: number): { station: CurrentStation; now: CurrentNow }[] {
    const out: { station: CurrentStation; now: CurrentNow }[] = [];
    for (const s of this.stations) {
      const now = currentAt(s, t);
      if (now) out.push({ station: s, now });
    }
    return out;
  }

  /** The [min, max] instant the shipped data covers, once loaded. Every
   * station spans the whole year, so the extremes are the dataset's. */
  get range(): [number, number] | null {
    let min = Infinity;
    let max = -Infinity;
    for (const s of this.stations) {
      const f = s.events[0];
      const l = s.events[s.events.length - 1];
      if (f && l) {
        if (f.t < min) min = f.t;
        if (l.t > max) max = l.t;
      }
    }
    return Number.isFinite(min) ? [min, max] : null;
  }

  /** The nearest stations to a position, for a strip or a panel. */
  near(pos: { lat: number; lon: number }, km = 60): CurrentStation[] {
    return near(this.stations, pos, km);
  }

  /** Whether the shipped dataset still covers `t`. It is one year of data. */
  covers(t: number): boolean {
    const s = this.stations[0];
    if (!s || s.events.length < 2) return false;
    return t >= s.events[0]!.t && t <= s.events[s.events.length - 1]!.t;
  }
}

export const currents = new Currents();
